/**
 * ONE VISIT, and the one flow that changes it.
 *
 * Two people open this page and see two different things, and the SERVER decided which. GET
 * /api/visits/:id answers a cleaner with her visit and who is on it with her, and the office with
 * the home, the household, the standing schedule and every change ever recorded. `isOfficeDetail`
 * only reads which answer came back; it never widens it.
 *
 * THE MOVE FLOW IS THE POINT OF THIS SCREEN. CLAUDE.md binding rule 7 and `docs/architecture.md`
 * section 6: a visit does not move until the change is recorded and the client has been told, or
 * a typed reason says how she already knows. So the form below asks two questions in order, what
 * is changing and how will she know, and the second one has exactly two answers:
 *
 *   Send her a message. The default sentence comes from the server, a person edits it and approves
 *   it, and the phone system delivers it. The visit stays where it is until delivery is reported,
 *   and the screen says so. No AI writes a word a client reads.
 *
 *   She already knows, or cannot be told. One of the typed reasons. The visit moves now.
 *
 * A change with neither is refused by the server, and the screen does not offer a third button.
 * When the phone-system seam is not connected yet (`me.canHandOffMessages` false), the first answer
 * is not drawn and one sentence says why, so the office records how she was told by hand.
 *
 * ONE CHANGE AT A TIME, AND THE SCREEN SAYS SO BEFORE THE SERVER DOES. A visit whose last change
 * is still waiting for the client to be told takes no second change: the server refuses it with
 * `change_pending`, because two records and one truth means nobody can say what she was told. The
 * whole move form used to be drawn over that anyway, every field of it, and every press of every
 * button came back refused after the sentence had been written. So while a change is waiting, what
 * stands in that panel is the change itself and the two ways out of it: the message goes and she
 * is told, or somebody types how she already knows. The form comes back when the change is
 * finished, which is the only moment it can work.
 *
 * ON A PHONE THAT IS ONE COLUMN, and it is unchanged: the visit, the household, the arrangement,
 * then the change flow with the one path she picked, then the history. ON A DESK the visit stands
 * on the left and the change flow on the right, 520px of it, WITH BOTH PATHS DRAWN AT ONCE. That
 * is the whole reason to sit down to this screen: the message she will read and the reason she
 * already knows are the two answers to one question, and seeing both side by side is how somebody
 * decides between them rather than discovering the second one after choosing the first. Each path
 * carries its own button, and pressing one is the choice. The button of the path the radio shows
 * is the form's submit control, so Enter in any box commits that same path and the keyboard can
 * never record something other than what the screen says is chosen.
 *
 * THE PREVIEW IS NOT FETCHED ON A PHONE UNLESS THE MESSAGE PATH IS CHOSEN. Drawing both paths is
 * what makes the sentence worth having in hand; below md only one is drawn, so only one is asked
 * for, and a person on mobile data pays for nothing she cannot see.
 *
 * THE PARTS LIVE IN `visit-detail/`, one module to a section of this screen. This module is the
 * screen itself and the one way in: the router and the tests import from here.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ApiError,
  getVisit,
  isOfficeDetail,
  type Me,
  type VisitDetail as VisitDetailShape,
} from '../api.ts';
import { Busy, ErrorNotice } from '../components/Notice.tsx';
import { CleanerView } from './visit-detail/CleanerView.tsx';
import { OfficeView } from './visit-detail/OfficeView.tsx';
import type { Outcome } from './visit-detail/shared.ts';

/** The three rules that read a change, exported from here as they always were. */
export { messageOnItsWay, messageThatFailed, waitingChange } from './visit-detail/shared.ts';

export function VisitDetail({ me }: { me: Me }) {
  const { id = '' } = useParams();
  const [detail, setDetail] = useState<VisitDetailShape | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setError(null);
    getVisit(id)
      .then((answer) => {
        if (live) setDetail(answer);
      })
      .catch((thrown: unknown) => {
        if (!live) return;
        setError(
          thrown instanceof ApiError
            ? thrown
            : new ApiError(0, 'error', 'That visit did not load.'),
        );
      });
    return () => {
      live = false;
    };
  }, [id, attempt]);

  const isCleaner = me.role === 'cleaner';
  const back = isCleaner ? (
    <Link
      to="/my-work"
      className="mb-3 inline-flex min-h-11 desk:min-h-9 items-center text-sm font-medium text-navy/70"
    >
      Back to your visits
    </Link>
  ) : (
    <Link
      to="/today"
      className="mb-3 inline-flex min-h-11 desk:min-h-9 items-center text-sm font-medium text-navy/70"
    >
      Back to today
    </Link>
  );

  if (error) {
    return (
      <div>
        {back}
        <ErrorNotice title="That visit did not load" message={error.message} onRetry={reload} />
      </div>
    );
  }

  if (!detail) {
    return (
      <div>
        {back}
        <Busy what="this visit" />
      </div>
    );
  }

  return (
    <div>
      {back}
      {isOfficeDetail(detail) ? (
        <OfficeView
          me={me}
          detail={detail}
          outcome={outcome}
          onChanged={(result) => {
            setOutcome(result);
            reload();
          }}
          onReload={reload}
        />
      ) : (
        <CleanerView me={me} detail={detail} />
      )}
    </div>
  );
}
